export interface MusicStatusLike {
  status?: string | null
}

export function isProcessing(music: MusicStatusLike) {
  return music.status === 'Processing' || music.status === 'Init'
}

export function canRetry(music: MusicStatusLike) {
  return music.status === 'Failed'
}

enum LibraryCategories {
  All = 'all',
  AiMusic = 'ai-music',
  AiCover = 'ai-cover',
  TextToSpeech = 'text-to-speech',
}

export { LibraryCategories as LIBRARY_CATEGORIES }

export type LibraryCategory = LibraryCategories

export const AI_MUSIC_LIBRARY_TYPES = ['Song', 'Instrumental', 'Vocal'] as const
export const AI_COVER_LIBRARY_TYPES = ['Cover'] as const
export const TEXT_TO_SPEECH_LIBRARY_TYPES = ['Tts'] as const
export const APP_MUSIC_LIST_EXCLUDED_TYPES = ['Cover', 'Tts'] as const

export type LibraryMusicType =
  | (typeof AI_MUSIC_LIBRARY_TYPES)[number]
  | (typeof AI_COVER_LIBRARY_TYPES)[number]
  | (typeof TEXT_TO_SPEECH_LIBRARY_TYPES)[number]

export function isExcludedFromAppMusicList(musicType?: string | null): boolean {
  return (
    !!musicType &&
    (APP_MUSIC_LIST_EXCLUDED_TYPES as readonly string[]).includes(musicType)
  )
}

export interface ParsedLibraryCategory {
  musicTypes?: readonly LibraryMusicType[]
}

export function parseLibraryCategory(
  category?: string | null,
): ParsedLibraryCategory {
  switch (category) {
    case LibraryCategories.AiMusic:
      return {
        musicTypes: AI_MUSIC_LIBRARY_TYPES,
      }
    case LibraryCategories.AiCover:
      return {
        musicTypes: AI_COVER_LIBRARY_TYPES,
      }
    case LibraryCategories.TextToSpeech:
      return {
        musicTypes: TEXT_TO_SPEECH_LIBRARY_TYPES,
      }
    case LibraryCategories.All:
    default:
      return {}
  }
}

export function getLibraryCategoryByMusicType(
  musicType?: string | null,
): LibraryCategory {
  if (
    musicType &&
    (AI_MUSIC_LIBRARY_TYPES as readonly string[]).includes(musicType)
  ) {
    return LibraryCategories.AiMusic
  }

  if (
    musicType &&
    (AI_COVER_LIBRARY_TYPES as readonly string[]).includes(musicType)
  ) {
    return LibraryCategories.AiCover
  }

  if (
    musicType &&
    (TEXT_TO_SPEECH_LIBRARY_TYPES as readonly string[]).includes(musicType)
  ) {
    return LibraryCategories.TextToSpeech
  }

  return LibraryCategories.All
}

export enum MusicReferenceFrom {
  LOCAL = 'local',
  YOUTUBE = 'youtube',
}

/**
 * 去掉歌词中的时间戳
 * @param lyrics 包含时间戳的歌词文本
 * @returns 去掉时间戳后的歌词文本
 */
export function removeTimestamps(lyrics: string): string {
  if (!lyrics) return lyrics

  return lyrics.replace(/\[(\d{2}|NaN):(\d{2}|NaN)\.(\d{2}|NaN)\]/g, '')
}

// 去掉只包含段落标签的行，如 [xx:xx.xx]Intro, [xx:xx.xx]Verse 等，但保留有实际歌词内容的行
export function removeSection(lyrics: string): string {
  if (!lyrics) return lyrics

  // 匹配时间戳后只跟段落标签的行：[时间戳]段落标签（段落标签后没有其他内容）
  const sectionOnlyPattern =
    /^\s*\[(?:\d{2}|NaN):(?:\d{2}|NaN)\.(?:\d{2}|NaN)\](?:intro|verse|pre-chorus|chorus|bridge|breakdown|outro)\s*$/i

  return lyrics
    .split('\n')
    .filter((line) => !sectionOnlyPattern.test(line))
    .join('\n')
}
